/**
 * Key Generation and Management Module
 *
 * This module handles the generation of RSA key pairs for user identity
 * and AES keys for data encryption. All cryptographic operations use
 * the Web Crypto API for security.
 */

// Algorithm constants
const RSA_ALGORITHM: RsaHashedKeyGenParams = {
  name: 'RSA-OAEP',
  modulusLength: 4096,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
};

const AES_ALGORITHM = {
  name: 'AES-GCM',
  length: 256,
};

const PBKDF2_ITERATIONS = 100000;

/**
 * Generate a new RSA key pair for user identity
 */
export async function generateRSAKeyPair(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    RSA_ALGORITHM,
    true, // extractable
    ['encrypt', 'decrypt']
  );
}

/**
 * Generate a new AES-256 key for data encryption
 */
export async function generateAESKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    AES_ALGORITHM,
    true, // extractable
    ['encrypt', 'decrypt']
  );
}

/**
 * Export RSA public key to base64 string (SPKI format)
 */
export async function exportPublicKey(publicKey: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey('spki', publicKey);
  return arrayBufferToBase64(exported);
}

/**
 * Import RSA public key from base64 string (SPKI format)
 */
export async function importPublicKey(base64Key: string): Promise<CryptoKey> {
  const keyBuffer = base64ToArrayBuffer(base64Key);
  return crypto.subtle.importKey(
    'spki',
    keyBuffer,
    RSA_ALGORITHM,
    true,
    ['encrypt']
  );
}

/**
 * Export RSA private key to base64 string (PKCS8 format)
 */
export async function exportPrivateKey(privateKey: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey('pkcs8', privateKey);
  return arrayBufferToBase64(exported);
}

/**
 * Import RSA private key from base64 string (PKCS8 format)
 */
export async function importPrivateKey(base64Key: string): Promise<CryptoKey> {
  const keyBuffer = base64ToArrayBuffer(base64Key);
  return crypto.subtle.importKey(
    'pkcs8',
    keyBuffer,
    RSA_ALGORITHM,
    true,
    ['decrypt']
  );
}

/**
 * Export AES key to base64 string (raw format)
 */
export async function exportAESKey(aesKey: CryptoKey): Promise<string> {
  const exported = await crypto.subtle.exportKey('raw', aesKey);
  return arrayBufferToBase64(exported);
}

/**
 * Import AES key from base64 string (raw format)
 */
export async function importAESKey(base64Key: string): Promise<CryptoKey> {
  const keyBuffer = base64ToArrayBuffer(base64Key);
  return crypto.subtle.importKey(
    'raw',
    keyBuffer,
    AES_ALGORITHM,
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Derive encryption key from password using PBKDF2
 */
export async function deriveKeyFromPassword(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  // Convert password to key material
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive AES key from password
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    AES_ALGORITHM,
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt private key with password for secure storage
 */
export async function encryptPrivateKey(
  privateKey: CryptoKey,
  password: string
): Promise<string> {
  // Generate salt for PBKDF2
  const salt = crypto.getRandomValues(new Uint8Array(16));

  // Derive encryption key from password
  const derivedKey = await deriveKeyFromPassword(password, salt);

  // Export private key
  const privateKeyData = await crypto.subtle.exportKey('pkcs8', privateKey);

  // Generate IV for AES-GCM
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // Encrypt the private key
  const encryptedData = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    derivedKey,
    privateKeyData
  );

  // Combine salt + iv + encrypted data
  const combined = new Uint8Array(salt.length + iv.length + encryptedData.byteLength);
  combined.set(salt, 0);
  combined.set(iv, salt.length);
  combined.set(new Uint8Array(encryptedData), salt.length + iv.length);

  return arrayBufferToBase64(combined.buffer);
}

/**
 * Decrypt private key with password
 */
export async function decryptPrivateKey(
  encryptedKey: string,
  password: string
): Promise<CryptoKey> {
  const combined = base64ToArrayBuffer(encryptedKey);
  const combinedArray = new Uint8Array(combined);

  // Extract salt, iv, and encrypted data
  const salt = combinedArray.slice(0, 16);
  const iv = combinedArray.slice(16, 28);
  const encryptedData = combinedArray.slice(28);

  // Derive decryption key from password
  const derivedKey = await deriveKeyFromPassword(password, salt);

  // Decrypt the private key
  const decryptedData = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    derivedKey,
    encryptedData
  );

  // Import the decrypted private key
  return crypto.subtle.importKey(
    'pkcs8',
    decryptedData,
    RSA_ALGORITHM,
    true,
    ['decrypt']
  );
}

/**
 * Encrypt AES key with RSA public key (for key sharing)
 */
export async function encryptKeyWithPublicKey(
  aesKey: CryptoKey,
  publicKey: CryptoKey
): Promise<string> {
  const aesKeyData = await crypto.subtle.exportKey('raw', aesKey);

  const encrypted = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    publicKey,
    aesKeyData
  );

  return arrayBufferToBase64(encrypted);
}

/**
 * Decrypt AES key with RSA private key (for key sharing)
 */
export async function decryptKeyWithPrivateKey(
  encryptedKey: string,
  privateKey: CryptoKey
): Promise<CryptoKey> {
  const encryptedData = base64ToArrayBuffer(encryptedKey);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'RSA-OAEP' },
    privateKey,
    encryptedData
  );

  return crypto.subtle.importKey(
    'raw',
    decrypted,
    AES_ALGORITHM,
    true,
    ['encrypt', 'decrypt']
  );
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Convert ArrayBuffer to base64 string
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Generate random bytes as hex string
 */
export function generateRandomHex(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
